import { create } from "zustand";

interface BreakSettings {
  workMinutes: number;   // parent-configurable, default 30
  breakMinutes: number;  // parent-configurable, default 10
}

interface SessionStore {
  // Active lesson tracking
  isInLesson: boolean;
  sessionId: string | null;
  studentId: string | null;
  lessonSecondsActive: number;  // only counts active (non-break) time

  // Break state
  isOnBreak: boolean;
  breakSecondsLeft: number;
  breakSettings: BreakSettings;

  // Posture / screen tracking (for report)
  postureWarnings: number;
  distanceWarnings: number;

  // Actions
  startLesson: (sessionId: string, studentId: string) => void;
  endLesson: () => void;
  tickActiveSecond: () => void;
  triggerBreak: () => void;
  tickBreakSecond: () => void;
  endBreak: () => void;
  recordPostureWarning: () => void;
  recordDistanceWarning: () => void;
  updateBreakSettings: (s: BreakSettings) => void;
}

export const useSessionStore = create<SessionStore>((set, get) => ({
  isInLesson: false,
  sessionId: null,
  studentId: null,
  lessonSecondsActive: 0,

  isOnBreak: false,
  breakSecondsLeft: 0,
  breakSettings: { workMinutes: 30, breakMinutes: 10 },

  postureWarnings: 0,
  distanceWarnings: 0,

  startLesson: (sessionId, studentId) =>
    set({ isInLesson: true, sessionId, studentId, lessonSecondsActive: 0, isOnBreak: false }),

  endLesson: () =>
    set({ isInLesson: false, sessionId: null, studentId: null, isOnBreak: false }),

  tickActiveSecond: () => {
    const { lessonSecondsActive, breakSettings, isOnBreak } = get();
    if (isOnBreak) return;
    const next = lessonSecondsActive + 1;
    const workSecs = breakSettings.workMinutes * 60;
    if (next >= workSecs) {
      // Time to break!
      set({ lessonSecondsActive: 0, isOnBreak: true, breakSecondsLeft: breakSettings.breakMinutes * 60 });
    } else {
      set({ lessonSecondsActive: next });
    }
  },

  triggerBreak: () => {
    const { breakSettings } = get();
    set({ isOnBreak: true, breakSecondsLeft: breakSettings.breakMinutes * 60, lessonSecondsActive: 0 });
  },

  tickBreakSecond: () => {
    const { breakSecondsLeft } = get();
    if (breakSecondsLeft <= 1) {
      set({ isOnBreak: false, breakSecondsLeft: 0 });
    } else {
      set({ breakSecondsLeft: breakSecondsLeft - 1 });
    }
  },

  endBreak: () => set({ isOnBreak: false, breakSecondsLeft: 0 }),

  recordPostureWarning: () => set((s) => ({ postureWarnings: s.postureWarnings + 1 })),
  recordDistanceWarning: () => set((s) => ({ distanceWarnings: s.distanceWarnings + 1 })),

  updateBreakSettings: (settings) => set({ breakSettings: settings }),
}));
